import { useMemo } from "react";

import { useCurrentOrg } from "_/data/orgs";
import { useCurrentUserPermissions } from "_/data/rbac";
import { permissions as rbacPermissions } from "_/data/rbac/definitions";
import { useMaterials, Material } from "_/data/materials";
import { useMachines, Machine } from "_/data/machines";
import { Project, useProjects, Revision } from "_/data/projects";
import { useUsers, User } from "_/data/users";
import {
  useDeletePrintQueueEntry,
  usePrintQueueEntries,
  useReorderPrintQueueEntry,
  PrintQueueEntry,
} from "_/data/print-queue";

import { Uuid } from "_/types";

export type QueueRow = {
  /**
   * Original print queue entry returned from the backend.
   */
  entry: PrintQueueEntry;

  /**
   * Unique identifier for the print queue entry row.
   */
  id: Uuid;

  /**
   * Queue priority position.
   */
  position: string;

  /**
   * When the entry was created.
   */
  createdAt: string;

  /**
   * Project metadata for the revision.
   */
  projectId?: Uuid;
  projectName?: string;

  /**
   * Revision metadata.
   */
  revisionId: Uuid;
  revisionNumber?: number;

  /**
   * Machine, if the entry is pinned to one.
   */
  machine?: Machine;

  /**
   * List of materials associated with the revision.
   */
  materials: Material[];

  /**
   * Who submitted this entry.
   */
  submittedBy?: User;
};

export function useQueueRows() {
  const queueResult = usePrintQueueEntries();
  const { data: projects = [] } = useProjects();
  const { data: machines = [] } = useMachines();
  const { data: materials = [] } = useMaterials();
  const { data: users = [] } = useUsers();
  const orgQuery = useCurrentOrg();
  const { data: userPermissions } = useCurrentUserPermissions(
    orgQuery.data?.domainId
  );
  const canEdit = userPermissions.includes(rbacPermissions.queue.edit);
  const canDelete = userPermissions.includes(rbacPermissions.queue.delete);

  const reorderMutation = useReorderPrintQueueEntry();
  const deleteMutation = useDeletePrintQueueEntry();

  const rows = useMemo(() => {
    const entries = queueResult.data ?? [];

    const machineById = new Map(machines.map((m) => [m.id, m]));
    const materialById = new Map(materials.map((m) => [m.id, m]));
    const userById = new Map(users.map((u) => [u.id, u]));

    const projectRevisionsById = new Map<
      Uuid,
      { project: Project; revision: Revision }
    >();
    projects.forEach((project) => {
      project.revisions.forEach((revision) => {
        projectRevisionsById.set(revision.id, { project, revision });
      });
    });

    const computeMaterials = (revision?: Revision) => {
      if (!revision) return [];
      const materialIds = [revision.materials[0], revision.materials[1]].filter(
        (id): id is Uuid => !!id
      );
      return materialIds
        .map((id) => materialById.get(id))
        .filter((mat): mat is Material => !!mat);
    };

    const queueRows: QueueRow[] = entries.map((entry) => {
      const revisionMeta = projectRevisionsById.get(entry.revisionId);
      const revision = revisionMeta?.revision;
      const project = revisionMeta?.project;

      const position = entry.rank ?? "";

      return {
        entry,
        id: entry.id,
        position,
        createdAt: entry.createdAt,
        projectId: project?.id,
        projectName: project?.name,
        revisionId: entry.revisionId,
        revisionNumber: revision?.number,
        machine: entry.machineId ? machineById.get(entry.machineId) : undefined,
        materials: computeMaterials(revision),
        submittedBy: userById.get(entry.submittedBy),
      };
    });

    return queueRows;
  }, [queueResult.data, projects, machines, materials, users]);

  const { data: entries = [], ...rest } = queueResult;

  const handleReorder = (
    entryId: string,
    bounds: { beforeId?: string; afterId?: string }
  ) => {
    if (!canEdit || reorderMutation.isPending) {
      return;
    }
    reorderMutation.mutate({ entryId, ...bounds });
  };

  const handleMoveToTop = (row: QueueRow) => {
    if (!canEdit || rows[0]?.id === row.id) {
      return;
    }
    handleReorder(row.id, { afterId: rows[0]?.id });
  };

  const handleDelete = (entryId: string) => {
    if (!canDelete) {
      return;
    }
    deleteMutation.mutate(entryId);
  };

  return {
    rows,
    entries,
    ...rest,
    canEdit,
    canDelete,
    isReordering: reorderMutation.isPending,
    onReorder: handleReorder,
    onMoveToTop: handleMoveToTop,
    onDelete: handleDelete,
  };
}
